import { useEffect, useState } from "react";
import { Plus, Minus } from "@phosphor-icons/react";

const FAQS = [
  {
    q: "Do I need an account to send a file?",
    a: "No. SendMe is anonymous by default — drop a file, get a 6-digit code, share. Accounts are optional and only unlock perks like transfer history, longer expiry, custom passwords on every transfer, and the team workspace. Receivers never need an account."
  },
  {
    q: "What's the maximum file size I can send?",
    a: "The free plan accepts files up to 100 MB per upload and supports multiple files per transfer. SendMe Pro raises the per-file limit to 5 GB and adds 100 GB of personal cloud storage. Team workspaces include 5 GB of shared storage."
  },
  {
    q: "How long does a transfer last?",
    a: "Free transfers last 24 hours by default and can be set as short as 1 hour. Pro users can pick anywhere from 1 hour to 7 days. After the expiry window passes, the file becomes inaccessible to receivers and is purged from our active storage shortly after."
  },
  {
    q: "Is the connection encrypted?",
    a: "Yes. Every byte travels over HTTPS (TLS 1.3) between your device and our servers. Files are stored on encrypted object storage and the storage URLs are non-guessable UUIDs that we never expose to receivers — they only see the 6-digit code or a one-time download link."
  },
  {
    q: "Can I password-protect a file?",
    a: "Yes. Toggle 'Add password' before uploading, then share the password with the receiver over a different channel than the code itself. We store only a bcrypt hash of the password — neither we nor anyone else can recover it if you forget."
  },
  {
    q: "How does the 6-digit code work?",
    a: "Each transfer is assigned a random 6-digit numeric code. The receiver visits sendme.co.in, types the code, and the file is served — optionally after entering the password you set. Codes are unique within their expiry window and retired immediately on expiry."
  },
  {
    q: "Do you scan files for malware?",
    a: "We don't routinely scan content because it would compromise your privacy. We do enforce strict acceptable-use rules (see Terms of Service) and respond to abuse reports. If you're receiving a file from someone you don't fully trust, always scan it locally with your antivirus before opening."
  },
  {
    q: "Can the receiver download the file more than once?",
    a: "Yes. Until the transfer expires (or the sender deletes it from their dashboard), the receiver can re-download as many times as they need. There's no per-download counter, but you'll see total downloads in your dashboard if you have an account."
  },
  {
    q: "What happens if I'm using the same code as someone else?",
    a: "You can't. Codes are guaranteed unique within their active window. If a code is in use, our system generates a fresh one — you'll never receive someone else's file when you type a code."
  },
  {
    q: "Is SendMe GDPR compliant?",
    a: "Yes. We store only what's necessary to provide the service, we delete on expiry, and we offer account-level deletion on request. We don't sell personal data and we don't share it with advertisers beyond what's required for serving Google AdSense on the blog (which uses non-personalised ads by default for EU visitors)."
  },
  {
    q: "Can I cancel my Pro subscription anytime?",
    a: "Anytime. Go to Profile → Billing → Cancel subscription. You'll keep Pro features until the end of the current billing period. We also offer a 7-day no-questions refund on your first payment."
  },
  {
    q: "Do you have an API?",
    a: "An official REST API is on the roadmap for Q3 2026, with first access going to Pro subscribers. If you have a specific automation need today, write to support@sendme.co.in — we sometimes whitelist beta integrations for active customers."
  }
];

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);

  // Inject FAQPage structured data so search engines and AdSense pick up the content.
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a }
      }))
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify(ld);
    s.dataset.faqJsonld = "1";
    document.head.appendChild(s);
    return () => {
      try { document.head.removeChild(s); } catch {}
    };
  }, []);

  return (
    <section className="mt-20 md:mt-28" data-testid="home-faq">
      <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-14 items-start">
        <div>
          <div className="small-caps mb-3">FAQ</div>
          <h2 className="text-3xl md:text-4xl tracking-tighter leading-[1.05] mb-3">Questions, answered.</h2>
          <p className="text-black/65 leading-relaxed max-w-sm">
            The twelve things people ask before sending their first file. If yours isn't here,
            read the <a className="underline" href="/help">help center</a> or write to{" "}
            <a className="underline" href="mailto:hello@sendme.co.in">hello@sendme.co.in</a>.
          </p>
        </div>
        <ul className="divide-y divide-black/10 border-t border-b border-black/10">
          {FAQS.map((f, i) => {
            const open = openIdx === i;
            return (
              <li key={f.q} data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full flex items-start justify-between gap-4 py-4 text-left brut-hover"
                  aria-expanded={open}
                >
                  <span className="font-bold tracking-tight text-[15px] md:text-base">{f.q}</span>
                  <span className="mt-1 text-black/50 flex-shrink-0">
                    {open ? <Minus size={16} weight="bold"/> : <Plus size={16} weight="bold"/>}
                  </span>
                </button>
                {open && (
                  <div className="pb-5 pr-8 text-[15px] text-black/70 leading-relaxed">
                    {f.a}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
